import { useEffect, useState } from 'react';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Mixpanel } from '../../services/Mixpanel';
import {
  createMemory,
  deleteMemory,
  getMemories,
  updateMemory,
} from '../../services/MemoryService';

// Memory manager (list, add, inline edit, delete), rendered as a section of
// EditProfileModal. Unlike the profile fields around it, every change here
// saves immediately — memories have their own CRUD endpoints and are not part
// of the form's single Save.

const MAX_MEMORY_LENGTH = 1000;

const TEXTAREA_CLASSES =
  'shadow-sm focus:ring-emerald-500 focus:border-emerald-500 block w-full sm:text-sm border-gray-300 rounded-md p-2.5';

export default function MemoriesSection() {
  const [memories, setMemories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newContent, setNewContent] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    getMemories()
      .then((res) => setMemories(res.data?.memories ?? []))
      .catch(() => setErrorMessage('There was an error loading your memories.'))
      .finally(() => setIsLoading(false));
  }, []);

  const handleAdd = async () => {
    const content = newContent.trim();
    if (!content) return;
    setIsSaving(true);
    setErrorMessage('');
    try {
      const res = await createMemory(content, 'manual');
      setMemories([...memories, res.data]);
      setNewContent('');
      Mixpanel.track('Memory Added', { source: 'manual' });
    } catch (e) {
      setErrorMessage(
        e.response?.data?.user_error_message || 'There was an error saving your memory.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    const content = editingContent.trim();
    if (!content || !editingId) return;
    setIsSaving(true);
    setErrorMessage('');
    try {
      const res = await updateMemory(editingId, content);
      setMemories(memories.map((m) => (m.id === editingId ? res.data : m)));
      setEditingId(null);
      setEditingContent('');
      Mixpanel.track('Memory Edited');
    } catch (e) {
      setErrorMessage(
        e.response?.data?.user_error_message || 'There was an error saving your memory.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (memoryId) => {
    setErrorMessage('');
    try {
      await deleteMemory(memoryId);
      setMemories(memories.filter((m) => m.id !== memoryId));
      Mixpanel.track('Memory Deleted');
    } catch (e) {
      setErrorMessage('There was an error deleting your memory.');
    }
  };

  return (
    <div className='flex flex-col text-left gap-4'>
      {isLoading ? (
        <p className='text-sm text-gray-500'>Loading…</p>
      ) : (
        <ul className='flex flex-col gap-2'>
          {memories.length === 0 && (
            <li className='text-sm text-gray-500 italic'>
              No memories yet. Add anything Diffit should always keep in mind — who your students
              are, or how you like your resources — below, or check “Remember this” when answering
              questions.
            </li>
          )}
          {memories.map((memory) => (
            <li
              key={memory.id}
              className='flex items-start gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2'>
              {editingId === memory.id ? (
                <div className='flex-1 flex flex-col gap-2'>
                  <textarea
                    rows={2}
                    maxLength={MAX_MEMORY_LENGTH}
                    className={TEXTAREA_CLASSES}
                    value={editingContent}
                    onChange={(e) => setEditingContent(e.target.value)}
                  />
                  <div className='flex gap-2'>
                    <button
                      type='button'
                      disabled={isSaving || !editingContent.trim()}
                      onClick={handleSaveEdit}
                      className='rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-500 disabled:bg-gray-300'>
                      Save
                    </button>
                    <button
                      type='button'
                      onClick={() => {
                        setEditingId(null);
                        setEditingContent('');
                      }}
                      className='rounded-md px-3 py-1.5 text-sm font-semibold text-black outline outline-1 outline-gray-300 bg-white hover:bg-gray-100'>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className='flex-1 text-sm text-gray-700 whitespace-pre-wrap'>
                    {memory.content}
                  </p>
                  <button
                    type='button'
                    aria-label='Edit memory'
                    onClick={() => {
                      setEditingId(memory.id);
                      setEditingContent(memory.content);
                    }}
                    className='p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded transition-colors'>
                    <PencilIcon className='w-4 h-4' />
                  </button>
                  <button
                    type='button'
                    aria-label='Delete memory'
                    onClick={() => handleDelete(memory.id)}
                    className='p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded transition-colors'>
                    <TrashIcon className='w-4 h-4' />
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className='flex flex-col gap-2'>
        <textarea
          id='new-memory'
          aria-label='Add a memory'
          rows={2}
          maxLength={MAX_MEMORY_LENGTH}
          placeholder='e.g. I have 3 Spanish speakers still learning English — or: Always include an answer key'
          className={TEXTAREA_CLASSES}
          value={newContent}
          onChange={(e) => setNewContent(e.target.value)}
        />
        <div className='flex justify-end'>
          <button
            type='button'
            disabled={isSaving || !newContent.trim()}
            onClick={handleAdd}
            className='inline-flex justify-center items-center font-semibold whitespace-nowrap gap-1 rounded-md bg-emerald-600 px-4 py-2 text-sm text-white hover:bg-emerald-500 disabled:bg-gray-300'>
            {isSaving ? 'Saving…' : 'Add Memory'}
          </button>
        </div>
      </div>

      {errorMessage && <p className='text-sm text-red-600'>{errorMessage}</p>}
    </div>
  );
}
